/**
 * Vocabulario de UI da fila do comprador (ADR-0069, 2026-09-14).
 *
 * Vive separado de `services/compras.js` porque os componentes de
 * visualizacao (tabela/kanban/linha do tempo/calendario) sao compartilhados
 * com a tela de Separacao e importam SITUACAO_LABEL/SITUACAO_TONE pelo mesmo
 * nome. Manter esses nomes aqui deixa os dois conjuntos de componentes
 * intercambiaveis sem renomear nada dentro deles.
 *
 * Seis situacoes GRAVADAS no backend (compras/models.py) — os codigos e os
 * tons espelham o catalogo de la; os rotulos sao os do mockup.
 */

export const SITUACOES_COMPRA = [
  { value: "RASCUNHO", label: "A cotar", tone: "warning", curto: "A cotar" },
  { value: "EM_COTACAO", label: "Em andamento", tone: "info", curto: "Em andamento" },
  { value: "APROVADA", label: "Aprovado", tone: "violet", curto: "Aprovado" },
  { value: "AGUARDANDO_ENTREGA", label: "Aguardando entrega", tone: "teal", curto: "Aguard. entrega" },
  { value: "ATENDIDA", label: "Recebido", tone: "success", curto: "Recebido" },
  { value: "CANCELADA", label: "Cancelado", tone: "neutral", curto: "Cancelado" },
];

// Consolidada (ADR-0074): o comprador juntou esta requisicao em outra. Fica
// FORA de SITUACOES_COMPRA de proposito — nao e coluna do quadro nem filtro;
// so aparece quando alguem abre a requisicao pelo link.
const CONSOLIDADA = { value: "CONSOLIDADA", label: "Consolidada", tone: "neutral", curto: "Consolidada" };

export const SITUACAO_LABEL = Object.fromEntries(
  [...SITUACOES_COMPRA, CONSOLIDADA].map((s) => [s.value, s.label]),
);
export const SITUACAO_TONE = Object.fromEntries(
  [...SITUACOES_COMPRA, CONSOLIDADA].map((s) => [s.value, s.tone]),
);

export const SITUACOES_TERMINAIS = new Set(["ATENDIDA", "CANCELADA", "CONSOLIDADA"]);

/** Ordem do stepper do drawer (Cancelado fica fora do trilho). */
export const STEPPER = SITUACOES_COMPRA.filter((s) => s.value !== "CANCELADA");

/** Colunas padrao do quadro — espelha `compras/kanban.py` (para "Restaurar padrao"). */
export const COLUNAS_PADRAO = [
  { id: "rascunho", titulo: "A cotar", cor: "#f59e0b", situacoes: ["RASCUNHO"], visivel: true },
  { id: "em_cotacao", titulo: "Em andamento", cor: "#2563eb", situacoes: ["EM_COTACAO"], visivel: true },
  { id: "aprovada", titulo: "Aprovado", cor: "#8b5cf6", situacoes: ["APROVADA"], visivel: true },
  { id: "aguardando_entrega", titulo: "Aguardando entrega", cor: "#14b8a6", situacoes: ["AGUARDANDO_ENTREGA"], visivel: true },
  { id: "atendida", titulo: "Recebido", cor: "#16a34a", situacoes: ["ATENDIDA"], visivel: true },
  { id: "cancelada", titulo: "Cancelado", cor: "#6b7280", situacoes: ["CANCELADA"], visivel: false },
];

/** Cor de cada segmento da barra de itens (README do handoff). */
export const COR_SEGMENTO_ITEM = {
  COMPRADO: "var(--success-solid)",
  EM_COTACAO: "var(--warning-solid)",
  APROVADO: "#8b5cf6",
  PENDENTE: "var(--border-strong)",
  CANCELADO: "var(--danger-solid)",
};

/** Tipos de evento da linha do tempo (espelha `compras/services_fila.py`). */
export const TIPOS_EVENTO = [
  { value: "status", label: "Status", tone: "info" },
  { value: "item", label: "Item", tone: "success" },
  { value: "comentario", label: "Comentário", tone: "neutral" },
  { value: "nova", label: "Nova REQ", tone: "warning" },
  { value: "cotacao", label: "Cotação", tone: "violet" },
  { value: "oc", label: "OC / entrega", tone: "teal" },
  { value: "anexo", label: "Anexo", tone: "neutral" },
  { value: "comprador", label: "Comprador", tone: "info" },
  { value: "prioridade", label: "Prioridade / prazo", tone: "warning" },
];
export const TIPO_EVENTO_LABEL = Object.fromEntries(TIPOS_EVENTO.map((t) => [t.value, t.label]));
export const TIPO_EVENTO_TONE = Object.fromEntries(TIPOS_EVENTO.map((t) => [t.value, t.tone]));

/** Texto curto do prazo: "atrasada 3d", "vence hoje", "em 4d". */
export function rotuloPrazo(dias) {
  if (dias === null || dias === undefined) return { texto: "sem prazo", tone: "neutral" };
  if (dias < 0) return { texto: `atrasada ${-dias}d`, tone: "danger" };
  if (dias === 0) return { texto: "vence hoje", tone: "warning" };
  return { texto: `em ${dias}d`, tone: "neutral" };
}

/** "4 dias em 'A cotar'" a partir de `situacao_alterada_em`. */
export function paradaHa(req) {
  if (!req?.situacao_alterada_em) return null;
  const ms = Date.now() - new Date(req.situacao_alterada_em).getTime();
  const dias = Math.max(0, Math.floor(ms / 86400000));
  const rotulo = SITUACAO_LABEL[req.situacao] || req.situacao;
  if (dias === 0) return `hoje em "${rotulo}"`;
  return `${dias} dia${dias === 1 ? "" : "s"} em "${rotulo}"`;
}
